"use client"

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../layout/ThemeProvider';

const SupportWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<{text: string; sender: 'bot' | 'user'}[]>([
    {text: 'Olá! Como posso ajudar você hoje?', sender: 'bot'}
  ]);
  const [inputValue, setInputValue] = useState('');
  const { theme } = useTheme();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Scroll to bottom of messages when a new message is added
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);
  
  // Pre-defined responses - COMPLETAMENTE ATUALIZADO
  const botResponses: Record<string, string> = {
    // Saudações
    'ola': 'Olá! Como posso ajudar você hoje?',
    'olá': 'Olá! Como posso ajudar você hoje?',
    'oi': 'Olá! Como posso ajudar você hoje?',
    'bom dia': 'Bom dia! Em que posso ajudar?',
    'boa tarde': 'Boa tarde! Como posso te auxiliar hoje?',
    'boa noite': 'Boa noite! Em que posso ser útil?',
    
    // Ajuda geral
    'ajuda': 'Claro! Estou aqui para ajudar. Você precisa de informações sobre nossos serviços, horários, localização ou outra coisa?',
    'suporte': 'Nosso suporte está à disposição. Como podemos ajudar com seus dispositivos?',
    
    // Informações de contato
    'contato': 'Você pode nos contatar pelo telefone (19) 3206-0706, WhatsApp (19) 99479-9400 ou pelo email contato@webimports.com.br',
    'telefone': 'Nosso telefone é (19) 3206-0706. Estamos à disposição!',
    'whatsapp': 'Você pode nos enviar mensagem no WhatsApp: (19) 99479-9400',
    'email': 'Nosso email é contato@webimports.com.br',
    
    // Horários
    'horario': 'Nosso horário de atendimento é de segunda a sexta das 9h às 18h, e aos sábados das 9h às 13h.',
    'horário': 'Nosso horário de atendimento é de segunda a sexta das 9h às 18h, e aos sábados das 9h às 13h.',
    'funcionamento': 'Estamos abertos de segunda a sexta das 9h às 18h, e aos sábados das 9h às 13h.',
    'aberto': 'Estamos abertos de segunda a sexta das 9h às 18h, e aos sábados das 9h às 13h.',
    
    // Localização
    'endereco': 'Estamos localizados na Rua Tapuru, 25, Alpha Mall / Loja 26, Alphaville, Campinas - SP, 13098-322',
    'endereço': 'Estamos localizados na Rua Tapuru, 25, Alpha Mall / Loja 26, Alphaville, Campinas - SP, 13098-322',
    'localizacao': 'Nossa loja fica na Rua Tapuru, 25, Alpha Mall / Loja 26, Alphaville, Campinas - SP, 13098-322',
    'localização': 'Nossa loja fica na Rua Tapuru, 25, Alpha Mall / Loja 26, Alphaville, Campinas - SP, 13098-322',
    'onde': 'Estamos em Campinas, na Rua Tapuru, 25, Alpha Mall / Loja 26, Alphaville, CEP 13098-322',
    
    // Serviços Gerais - ATUALIZADO
    'servicos': 'Oferecemos diversos serviços especializados: Desenvolvimento Web, Desenvolvimento de Sistemas, Infraestrutura de Rede, Energia Solar, Manutenção de Celulares e Tablets, Manutenção de Notebooks e PCs, Manutenção de Videogames, Manutenção de Eletrônicos e Automação Residencial. Sobre qual serviço você gostaria de saber mais?',
    'serviços': 'Oferecemos diversos serviços especializados: Desenvolvimento Web, Desenvolvimento de Sistemas, Infraestrutura de Rede, Energia Solar, Manutenção de Celulares e Tablets, Manutenção de Notebooks e PCs, Manutenção de Videogames, Manutenção de Eletrônicos e Automação Residencial. Sobre qual serviço você gostaria de saber mais?',
    
    // Desenvolvimento Web
    'desenvolvimento web': 'Criamos websites exclusivos e personalizados com foco em SEO, design moderno e segurança avançada, sem uso de templates ou módulos genéricos.',
    'site': 'Desenvolvemos sites exclusivos e personalizados, com foco em SEO, design moderno e segurança avançada.',
    'website': 'Nossos websites são totalmente personalizados, com design moderno e otimização para mecanismos de busca.',
    'web': 'Nossa equipe especializada cria websites exclusivos e personalizados sem o uso de templates ou módulos genéricos. Focamos em SEO, design moderno e segurança avançada.',
    
    // Desenvolvimento de Sistemas
    'desenvolvimento de sistemas': 'Criamos sistemas personalizados, integrações, dashboards e soluções para gerenciamento empresarial adaptados às necessidades específicas do seu negócio.',
    'sistema': 'Nossas soluções de sistemas incluem criação de software personalizado, integrações e dashboards para gerenciamento empresarial.',
    'software': 'Desenvolvemos software personalizado para necessidades específicas de empresas, com foco em usabilidade e eficiência.',
    'dashboard': 'Criamos dashboards intuitivos e personalizados para visualização de dados e gestão empresarial.',
    
    // Infraestrutura de Rede
    'infraestrutura de rede': 'Oferecemos projetos completos de infraestrutura de rede para empresas de todos os portes, garantindo conectividade confiável e 99.9% de disponibilidade.',
    'rede': 'Nossas soluções de infraestrutura de rede atendem empresas de todos os portes, com projetos completos e alta disponibilidade.',
    'wi-fi': 'Implementamos redes Wi-Fi robustas e seguras para ambientes corporativos e residenciais.',
    'internet': 'Nossos projetos de infraestrutura garantem conectividade estável e segura para sua empresa.',
    'infraestrutura': 'Nosso serviço de infraestrutura de rede oferece projetos completos para empresas, garantindo conectividade confiável e 99.9% de disponibilidade.',
    'conectividade': 'Oferecemos soluções completas de conectividade e redes para empresas, com foco em alta disponibilidade e segurança.',
    
    // Energia Solar
    'energia solar': 'Oferecemos soluções de energia solar para residências, comércios e indústrias, com instalação e manutenção de painéis fotovoltaicos que proporcionam excelente custo-benefício.',
    'solar': 'Nossos serviços de energia solar incluem instalação e manutenção de painéis fotovoltaicos para residências, comércios e indústrias.',
    'painel solar': 'Instalamos e fazemos manutenção de painéis fotovoltaicos com excelente custo-benefício para diversos tipos de imóveis.',
    'fotovoltaico': 'Nossos sistemas fotovoltaicos oferecem excelente retorno sobre investimento e são instalados com garantia de qualidade.',
    
    // Manutenção de Celulares e Tablets
    'manutenção de celulares': 'Realizamos serviços de reparo para smartphones e tablets de todas as marcas, com diagnóstico especializado e garantia de serviço.',
    'conserto de celular': 'Oferecemos conserto especializado para smartphones de todas as marcas, com diagnóstico preciso e soluções eficientes.',
    'reparo de tablet': 'Nossos técnicos são especializados em reparos de tablets de diversas marcas, com diagnóstico detalhado.',
    'celular': 'Realizamos manutenção e reparo de smartphones de todas as marcas com diagnóstico especializado.',
    'tablet': 'Consertamos tablets de todas as marcas com diagnóstico especializado e garantia de serviço.',
    
    // Manutenção de Notebooks e PCs
    'manutenção de notebooks': 'Oferecemos diagnóstico e solução para problemas de hardware e software em computadores portáteis e desktops.',
    'conserto de computador': 'Nossos serviços incluem reparo de computadores desktop e notebooks, com soluções para problemas de hardware e software.',
    'notebook': 'Realizamos reparos e manutenção em notebooks de todas as marcas, solucionando problemas de hardware e software.',
    'pc': 'Nossos serviços de manutenção para PCs incluem diagnóstico e reparo de problemas de hardware e software.',
    'computador': 'Oferecemos serviços completos de manutenção para computadores, incluindo reparo de hardware e solução de problemas de software.',
    
    // Manutenção de Videogames
    'manutenção de videogames': 'Fazemos reparos especializados para consoles de videogame de todas as gerações com garantia de serviço.',
    'conserto de videogame': 'Nossos técnicos são especializados em reparos de consoles de videogame de todas as gerações e marcas.',
    'reparo de console': 'Oferecemos serviços de reparo para consoles de videogame com garantia e qualidade.',
    'playstation': 'Realizamos reparos especializados em consoles PlayStation de todas as gerações.',
    'xbox': 'Oferecemos manutenção e reparo para Xbox de todas as gerações com diagnóstico especializado.',
    'nintendo': 'Consertamos consoles Nintendo com equipe especializada e garantia de serviço.',
    'videogame': 'Reparamos consoles de videogame de todas as gerações com garantia de serviço.',
    
    // Manutenção de Eletrônicos
    'manutenção de eletrônicos': 'Prestamos serviços de reparo para dispositivos eletrônicos diversos que necessitam de cuidados específicos.',
    'eletrônicos': 'Nossa equipe realiza reparos em diversos dispositivos eletrônicos, com técnicas especializadas para cada tipo de equipamento.',
    'aparelho': 'Consertamos aparelhos eletrônicos diversos com cuidados específicos para cada tipo de tecnologia.',
    
    // Automação Residencial
    'automação residencial': 'Transformamos sua casa em um ambiente inteligente com sistemas personalizados de automação e integração.',
    'casa inteligente': 'Implementamos soluções de automação residencial para criar ambientes inteligentes, práticos e eficientes.',
    'smart home': 'Nossas soluções de smart home transformam sua residência com automação personalizada e integração de sistemas.',
    'automação': 'Oferecemos serviços de automação para residências e empresas, com sistemas personalizados e integrados.',
    'casa automatizada': 'Transformamos sua casa em um ambiente automatizado, com controle de iluminação, temperatura, segurança e entretenimento.',
    
    // Microsolda (citado no resumo do projeto)
    'microsolda': 'Oferecemos serviços de microsolda especializada para recuperação de placas eletrônicas, utilizando técnicas avançadas de microsolda e reballing BGA.',
    'solda': 'Realizamos solda especializada em componentes eletrônicos, utilizando técnicas avançadas para recuperação de placas.',
    'reballing': 'Nossa equipe é especializada em reballing BGA e microsolda para recuperação de placas eletrônicas.',
    
    // Preços
    'precos': 'Os preços variam conforme o serviço e as necessidades específicas. Poderia especificar qual serviço você tem interesse para fornecermos um orçamento personalizado?',
    'preços': 'Os preços variam conforme o serviço e as necessidades específicas. Poderia especificar qual serviço você tem interesse para fornecermos um orçamento personalizado?',
    'valor': 'Os valores são personalizados de acordo com as necessidades de cada cliente. Poderia nos informar qual serviço específico você precisa para enviarmos um orçamento detalhado?',
    'orçamento': 'Para um orçamento preciso, precisamos entender suas necessidades específicas. Por favor, entre em contato pelo WhatsApp (19) 99479-9400 com detalhes do que você precisa.',
    'quanto': 'Os valores dependem das especificações do serviço. Entre em contato pelo WhatsApp (19) 99479-9400 para um orçamento personalizado.',
    
    // Acompanhamento de serviço
    'status': 'O acompanhamento de serviços em tempo real estará disponível em breve! Por enquanto, você pode entrar em contato pelo WhatsApp (19) 99479-9400 para verificar o status do seu serviço.',
    'acompanhar': 'O sistema de acompanhamento de serviços será implementado em breve! Por enquanto, entre em contato pelo WhatsApp (19) 99479-9400 para atualizações.',
    'andamento': 'Para verificar o andamento do seu serviço, por favor, entre em contato pelo WhatsApp (19) 99479-9400. Em breve teremos uma plataforma para acompanhamento em tempo real!',
    
    // Agradecimentos
    'obrigado': 'De nada! Estamos sempre à disposição para ajudar!',
    'obrigada': 'De nada! Estamos sempre à disposição para ajudar!',
    'valeu': 'Disponha! Precisando de mais algo, estamos aqui.',
    'grato': 'É um prazer ajudar! Estamos à disposição sempre que precisar.',
    'grata': 'É um prazer ajudar! Estamos à disposição sempre que precisar.',

    // Área do cliente
    'area do cliente': 'Nossa área do cliente estará disponível em breve! Por enquanto, você pode entrar em contato pelo WhatsApp (19) 99479-9400 para qualquer informação sobre seus serviços.',
    'área do cliente': 'Nossa área do cliente estará disponível em breve! Por enquanto, você pode entrar em contato pelo WhatsApp (19) 99479-9400 para qualquer informação sobre seus serviços.',
    'login': 'O sistema de login para área do cliente estará disponível em breve! Enquanto isso, fale conosco pelo WhatsApp (19) 99479-9400.',
    'cadastro': 'O sistema de cadastro para área do cliente estará disponível em breve! Enquanto isso, fale conosco pelo WhatsApp (19) 99479-9400.'
  };
  
  // Fallback function for unrecognized messages - ATUALIZADO
  const getFallbackResponse = () => {
    const fallbacks = [
      'Desculpe, não entendi completamente. Para melhor atendimento, entre em contato pelo WhatsApp (19) 99479-9400.',
      'Hmm, não tenho certeza do que você está perguntando. Para assistência personalizada, fale conosco pelo WhatsApp (19) 99479-9400.',
      'Esse assunto requer atenção especial. Por favor, envie sua dúvida pelo WhatsApp (19) 99479-9400 para um atendimento completo.',
      'Para tratar desse assunto com mais detalhes, recomendo entrar em contato pelo WhatsApp (19) 99479-9400. Nossa equipe terá prazer em ajudar.'
    ];
    return fallbacks[Math.floor(Math.random() * fallbacks.length)];
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    
    // Add user message
    setMessages(prev => [...prev, {text: inputValue, sender: 'user'}]);
    const userInput = inputValue;
    setInputValue('');
    
    // Simulate "typing..."
    setTimeout(() => {
      // Generate bot response
      let botResponse = '';
      
      // Check for keyword matches
      const userMessageLower = userInput.toLowerCase();
      let matched = false;
      
      for (const keyword in botResponses) {
        if (userMessageLower.includes(keyword)) {
          botResponse = botResponses[keyword];
          matched = true;
          break;
        }
      }
      
      // If no match, use generic response
      if (!matched) {
        botResponse = getFallbackResponse();
      }
      
      setMessages(prev => [...prev, {text: botResponse, sender: 'bot'}]);
    }, 1000);
  };
  
  return (
    <div 
      className={`fixed bottom-6 left-6 z-50 ${
        isOpen 
          ? 'w-80 h-96 md:w-96 md:h-[450px]' 
          : 'w-16 h-16'
      } transition-all duration-300 rounded-2xl shadow-xl overflow-hidden`}
      style={{
        backgroundColor: theme === 'dark' ? '#1f2937' : 'white',
        borderColor: theme === 'dark' ? '#374151' : '#e5e7eb',
        borderWidth: isOpen ? '1px' : '0px'
      }}
    >
      {/* Toggle button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className={`absolute ${isOpen ? 'top-3 right-3 w-8 h-8 bg-opacity-10' : 'inset-0 w-full h-full'} rounded-full text-white flex items-center justify-center z-20`}
        aria-label={isOpen ? "Fechar chat de suporte" : "Abrir chat de suporte"}
        style={{
          backgroundColor: isOpen 
            ? (theme === 'dark' ? '#4b5563' : '#e5e7eb') 
            : '#3b82f6'
        }}
      >
        {isOpen ? (
          <svg className="w-4 h-4" fill="none" stroke={theme === 'dark' ? 'white' : 'black'} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        )}
      </motion.button>
      
      {/* Chat content */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="w-full h-full flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div 
              className="p-4 flex items-center"
              style={{
                backgroundColor: theme === 'dark' ? '#111827' : '#3b82f6', 
                color: 'white'
              }}
            >
              <h3 className="font-semibold text-lg">Suporte Web Imports</h3>
            </div>
            
            {/* Message area */}
            <div 
              className="flex-1 p-4 overflow-y-auto"
              style={{
                backgroundColor: theme === 'dark' ? '#1f2937' : 'white'
              }}
            >
              {messages.map((msg, index) => (
                <div 
                  key={index} 
                  className={`max-w-[75%] mb-3 ${msg.sender === 'user' ? 'ml-auto' : 'mr-auto'}`}
                >
                  <div 
                    className={`p-3 rounded-lg ${
                      msg.sender === 'user' 
                        ? (theme === 'dark' ? 'bg-blue-800 text-white' : 'bg-blue-500 text-white') 
                        : (theme === 'dark' ? 'bg-gray-700 text-gray-100' : 'bg-gray-100 text-gray-800')
                    }`}
                  >
                    {msg.text}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
            
            {/* Message input */}
            <form 
              onSubmit={handleSubmit}
              className="p-3 border-t flex"
              style={{
                borderColor: theme === 'dark' ? '#374151' : '#e5e7eb',
                backgroundColor: theme === 'dark' ? '#1f2937' : 'white'
              }}
            >
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Digite sua mensagem..."
                className="flex-1 px-3 py-2 rounded-l-lg focus:outline-none"
                style={{
                  backgroundColor: theme === 'dark' ? '#111827' : '#f3f4f6',
                  color: theme === 'dark' ? 'white' : 'black',
                  borderColor: theme === 'dark' ? '#374151' : '#e5e7eb'
                }}
                aria-label="Digite sua mensagem"
              />
              <button
                type="submit"
                className="px-4 py-2 rounded-r-lg flex items-center justify-center text-white"
                style={{
                  backgroundColor: '#3b82f6'
                }}
                aria-label="Enviar mensagem"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SupportWidget;